import type { NotificationView } from '@chukua/common';
import { Badge, Popover, Tooltip } from 'antd';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { notificationPath } from '../policies/notification-path';
import { NotificationList } from '../components/NotificationList';
import { useNotifications } from '../hooks/use-notifications';

/** The in-app inbox: a bell with the unread count, opening the recent notifications. Mounted in the rail footer and the phone header. */
export function NotificationBell({ placement = 'right' }: { placement?: 'right' | 'bottomRight' }) {
  const { unread, listState, load, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (next) void load();
  }

  async function select(notification: NotificationView) {
    const path = notificationPath(notification);
    try {
      await markRead(notification);
    } catch {
      // Marking read is housekeeping: failing to must not stop someone getting to the job.
    }
    setOpen(false);
    if (path) navigate(path);
  }

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger="click"
      placement={placement}
      title="Notifications"
      content={<NotificationList state={listState} onSelect={select} />}
    >
      <Tooltip title="Notifications" placement={placement === 'right' ? 'right' : 'bottom'}>
        <button
          type="button"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          className="grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-sider-selected hover:text-fg max-md:size-10"
        >
          <Badge count={unread} size="small" overflowCount={99}>
            <Bell className="size-4 text-current" />
          </Badge>
        </button>
      </Tooltip>
    </Popover>
  );
}
